import React, { useEffect, useState, useMemo } from "react";
import { IoIosArrowForward } from "react-icons/io";
import { useParams, useLocation, Link } from "react-router-dom";
import { slugify } from "../../utils/slug";
import axios from "axios";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";
import { ShimmerPostDetails } from "react-shimmer-effects";
import { message, Modal, Button, Tooltip } from "antd";
import useUserStore from "../../store/useUserStrore";
import { printReportHTML } from "../../utils/printUtils.js";
import "./ProductDescriptionPage.css";

const ProductDescriptionPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const { userDetails } = useUserStore();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);

  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentGiven, setConsentGiven] = useState(() => {
    const storedConsent = localStorage.getItem("isConsent");
    return storedConsent === "true";
  });

  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showPublicReportModal, setShowPublicReportModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [publicReportUrl, setPublicReportUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const [showPdfModal, setShowPdfModal] = useState(false);

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    gender: "Male",
    age: "",
  });

  const [publicReportData, setPublicReportData] = useState({
    shareData: false,
    name: "",
    email: "",
    phone: "",
  });

  const fromMeasure =
    new URLSearchParams(location.search).get("fromMeasure") === "true";
  const capturedImage = localStorage.getItem("capturedImage");
  const capturedMM =
    new URLSearchParams(location.search).get("nostrilWidth") ||
    localStorage.getItem("capturedNostrilWidthMM");

  const handlePrintClick = () => {
    setShowPrintModal(true);
  };

  useEffect(() => {
    const handlePrintKey = (e) => {
      if (!fromMeasure) return;

      if ((e.ctrlKey || e.metaKey) && e.key === "p") {
        e.preventDefault();
        handlePrintClick();
      }
    };

    document.addEventListener("keydown", handlePrintKey);
    return () => {
      document.removeEventListener("keydown", handlePrintKey);
    };
  }, [fromMeasure]);

  useEffect(() => {
    const fetchProduct = async () => {
      const token = localStorage.getItem("userToken");
      try {
        // No token (logged-out visitor from a shared /vendor page link):
        // use the unauthenticated endpoint instead of sending a Bearer
        // header that would 401. Its payload has fewer fields (no vendor
        // name string, no pdfLink, no notAvail) but the same product shape.
        if (!token) {
          const response = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/open/v1/products/${id}`
          );
          setProduct(response.data.product);
          setSuggestions(response.data.product.suggestions || []);
          setLoading(false);
          return;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/auth/v1/product/${id}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setProduct(response.data.ProductItem);
        setSuggestions(response.data.ProductItem.suggestions || []);
        setLoading(false);
      } catch (error) {
        setError(
          error.response?.data?.message ||
            error.message ||
            "Failed to fetch product"
        );
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  const isSuggestionNA = useMemo(() => {
    const na = new Set(product?.notAvail || []);
    return (alpha) => na.has(alpha);
  }, [product]);

  const primaryImage = useMemo(
    () => product?.images?.[0] || "",
    [product?.images]
  );

  const handleInputChange = (e) =>
    setFormData((s) => ({ ...s, [e.target.name]: e.target.value }));

  const handlePublicReportInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPublicReportData((s) => ({
      ...s,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleImageUpload = async (imageFile) => {
    try {
      const fd = new FormData();
      fd.append("image", imageFile);
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/v1/upload-img`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data.fileUrl;
    } catch (error) {
      console.error("Error uploading image:", error);
      throw error;
    }
  };

  const base64ToBlob = (base64String) => {
    try {
      const byteCharacters = atob(base64String.split(",")[1]);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      return new Blob([new Uint8Array(byteNumbers)], { type: "image/jpeg" });
    } catch {
      return null;
    }
  };

  const createPublicReport = async () => {
    if (!formData.age) {
      message.error("Age is required for public report");
      return;
    }

    setIsUploading(true);
    try {
      let snapshotUrl = "";
      if (capturedImage) {
        const snapshotBlob = base64ToBlob(capturedImage);
        if (snapshotBlob) {
          snapshotUrl = await handleImageUpload(snapshotBlob);
        }
      }

      const suggestionImageUrls = [];
      for (const suggestion of suggestions) {
        if (suggestion.images?.length > 0) {
          suggestionImageUrls.push(...suggestion.images);
        }
      }

      const requestData = {
        userId: userDetails?._id || localStorage.getItem("userId"),
        age: parseInt(formData.age),
        nostrilWidth: parseFloat(capturedMM),
        snapshot: snapshotUrl,
        productImageUrl: primaryImage || "",
        suggestionProductImageUrl: suggestionImageUrls,
        isConsent: consentGiven,
      };

      if (publicReportData.shareData) {
        if (publicReportData.name) requestData.name = publicReportData.name;
        if (publicReportData.email) requestData.email = publicReportData.email;
        if (publicReportData.phone) requestData.phone = publicReportData.phone;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/v1/public-report`,
        requestData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.status && response.data.newPublicReport) {
        const reportId = response.data.newPublicReport._id;
        const publicUrl = `${window.location.origin}/public-report/${reportId}`;
        setPublicReportUrl(publicUrl);
        setShowPublicReportModal(false);
        setShowSuccessModal(true);
        message.success("Public report created successfully!");
      } else {
        message.error("Failed to create public report");
      }
    } catch (error) {
      console.error("Error creating public report:", error);
      message.error("Failed to create public report");
    } finally {
      setIsUploading(false);
    }
  };

  const handleConsentConfirm = () => {
    if (!consentGiven) {
      message.error("You must check consent before proceeding.");
      return;
    }
    setShowConsentModal(false);
    setShowPrintModal(true);
  };

  const handleModalSubmitAndPrint = () => {
    if (!formData.age) {
      message.error("Age is required to print the report.");
      return;
    }
    localStorage.setItem("savedFormData", JSON.stringify(formData));
    setShowPrintModal(false);

    const printData = {
      capturedImage: capturedImage,
      mainProductImage: primaryImage,
      suggestions: suggestions,
      userInfo: {
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        gender: formData.gender,
        age: formData.age,
        nostrilWidth: capturedMM,
      },
      productInfo: {
        name: product.name,
        alphaCode: product.alphaCode,
        vendor: product.vendor,
        shape: product.shape,
        case: product.case,
        color: product.color,
        height: product.height,
        width: product.width,
        mass: product.mass,
      },
    };

    setTimeout(() => {
      printReportHTML(printData);
    }, 300);
  };

  const handleOpenPdf = () => setShowPdfModal(true);
  const handleClosePdf = () => setShowPdfModal(false);

  const handleDownloadPdf = () => {
    if (!product?.pdfLink) return;
    const a = document.createElement("a");
    a.href = product.pdfLink;
    a.download = `${product.alphaCode || product.name || "report"}.pdf`;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleCopyPdfLink = async () => {
    if (!product?.pdfLink) return;
    try {
      await navigator.clipboard.writeText(product.pdfLink);
      message.success("PDF link copied to clipboard");
    } catch {
      message.error("Failed to copy link");
    }
  };

  if (loading)
    return (
      <div className="shimmer-loading">
        <ShimmerPostDetails card imageHeight={300} caption />
      </div>
    );
  if (error) return <div className="error-message">Error: {error}</div>;
  if (!product) return <div className="error-message">No product found</div>;

  return (
    <div className="description-container">
      <div className="breadcrumb">
        <a href="/">Home</a>
        <span>
          <IoIosArrowForward />
        </span>
        <a href={`/product-description/${product._id}`}>{product.name}</a>
      </div>

      <div id="pdf-content">
        <div className="product-top">
          <div className="product-images">
            {fromMeasure && capturedImage && (
              <div className="captured-block">
                <h4>Captured Face Snapshot</h4>
                <Zoom>
                  <img
                    loading="lazy"
                    src={capturedImage}
                    alt="Captured Face"
                    className="product-image"
                  />
                </Zoom>
              </div>
            )}

            {product.images?.length ? (
              product.images.map((image, index) => (
                <Zoom key={index}>
                  <img
                    loading="lazy"
                    src={image}
                    alt={`Product ${index}`}
                    className="product-image"
                  />
                </Zoom>
              ))
            ) : (
              <div className="no-image">No images available</div>
            )}
          </div>

          <div className="product-identity">
            <div className="product-identity-head">
              <h1 className="product-name">{product.name}</h1>
              {product.alphaCode && (
                <span className="alpha-code">{product.alphaCode}</span>
              )}
            </div>

            <div className="meta-grid">
              {product.vendor ? (
                <Link
                  className="meta-pill-link"
                  to={`/vendor/${
                    product.vendorSlug || slugify(product.vendor)
                  }`}
                >
                  <MetaPill label="Vendor" value={product.vendor} />
                </Link>
              ) : (
                <MetaPill label="Vendor" value="—" />
              )}
              <MetaPill label="Shape" value={product.shape || "—"} />
              <MetaPill label="Case" value={product.case || "—"} />
              <MetaPill label="Color" value={product.color || "—"} />
              {/* The unit belongs in the value, not only the label: "37 x 36"
                  on its own says nothing, and the label already has to
                  carry which of the two is height. */}
              <MetaPill
                label="Size (H × W)"
                value={
                  product.height && product.width
                    ? `${product.height} × ${product.width} mm`
                    : "—"
                }
              />
              <MetaPill
                label="Mass"
                value={product.mass ? `${product.mass} g` : "—"}
              />
            </div>

            {product.pdfLink && (
              <div className="pdf-actions">
                <Button type="primary" onClick={handleOpenPdf}>
                  View PDF
                </Button>
                <Button onClick={handleDownloadPdf}>Download PDF</Button>
                <Button onClick={handleCopyPdfLink}>Copy PDF Link</Button>
              </div>
            )}
          </div>
        </div>

        {fromMeasure && (
          <div className="patient-info-box">
            <h2>Patient & Denture Measurement Report</h2>
            <div className="info-grid">
              <div className="info-card light-blue">
                <label>Age</label>
                <span>{formData.age || "—"}</span>
              </div>
              <div className="info-card light-blue">
                <label>Nostrils Width</label>
                <span>{capturedMM ? `${capturedMM} mm` : "—"}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {fromMeasure && (
        <div className="print-section">
          <button className="print-button" onClick={handlePrintClick}>
            Download Report
          </button>
        </div>
      )}

      <div className="suggestions-section">
        <div className="suggestions-header">
          <h3>Related Products</h3>
          {!!product?.notAvail?.length && (
            <Tooltip title="These alpha codes are currently not available">
              <span className="unavailable-note">
                Unavailable: {product.notAvail.join(", ")}
              </span>
            </Tooltip>
          )}
        </div>

        <div className="suggestions-container">
          {suggestions.length > 0 ? (
            suggestions.map((item, index) => (
              <div key={index} className="suggestion-item">
                <div className="suggestion-item-head">
                  <h4>{item.name}</h4>
                  {item.alphaCode && (
                    <span
                      className={
                        isSuggestionNA(item.alphaCode)
                          ? "suggestion-availability is-unavailable"
                          : "suggestion-availability is-available"
                      }
                    >
                      {item.alphaCode}{" "}
                      {isSuggestionNA(item.alphaCode)
                        ? "• Not Available"
                        : "• In Catalog"}
                    </span>
                  )}
                </div>

                {item.images?.length ? (
                  item.images.map((img, imgIndex) => (
                    <Zoom key={imgIndex}>
                      <img
                        loading="lazy"
                        src={img}
                        alt={`Suggestion ${imgIndex}`}
                        className="suggestion-image"
                      />
                    </Zoom>
                  ))
                ) : (
                  <p className="suggestion-no-image">No images</p>
                )}
              </div>
            ))
          ) : (
            <p>No suggestions available</p>
          )}
        </div>
      </div>

      {showPrintModal && (
        <div className="modal-overlay">
          <div className="modal-content beautiful-modal">
            <h3>Enter Details for Report (Required)</h3>
            <input
              type="number"
              name="age"
              placeholder="Age (Required)"
              value={formData.age}
              onChange={handleInputChange}
              required
            />
            <input
              type="text"
              name="fullName"
              placeholder="Full Name (Optional)"
              value={formData.fullName}
              onChange={handleInputChange}
            />
            <input
              type="email"
              name="email"
              placeholder="Email (Optional)"
              value={formData.email}
              onChange={handleInputChange}
            />
            <input
              type="tel"
              name="phone"
              placeholder="Phone (Optional)"
              value={formData.phone}
              onChange={handleInputChange}
            />
            <select
              name="gender"
              value={formData.gender}
              onChange={handleInputChange}
            >
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
            <div className="modal-actions">
              <button
                className="confirm-btn"
                onClick={handleModalSubmitAndPrint}
              >
                Print Report
              </button>
              <button
                className="cancel-btn"
                onClick={() => setShowPrintModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {product?.pdfLink && (
        <Modal
          title={`PDF Preview — ${product.alphaCode || product.name}`}
          open={showPdfModal}
          onCancel={handleClosePdf}
          footer={
            <div
              style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
            >
              <Button onClick={handleCopyPdfLink}>Copy Link</Button>
              <Button onClick={handleDownloadPdf}>Download</Button>
              <Button type="primary" onClick={handleClosePdf}>
                Close
              </Button>
            </div>
          }
          width="90%"
          style={{ top: 24 }}
          bodyStyle={{ height: "75vh", padding: 0 }}
          destroyOnClose
        >
          <div style={{ height: "100%", width: "100%" }}>
            <object
              data={product.pdfLink}
              type="application/pdf"
              width="100%"
              height="100%"
            >
              <iframe
                src={product.pdfLink}
                title="PDF Preview"
                style={{ width: "100%", height: "100%", border: "none" }}
              />
            </object>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ProductDescriptionPage;

function MetaPill({ label, value }) {
  return (
    <div className="meta-pill">
      <span className="meta-pill-label">{label}</span>
      <strong className="meta-pill-value">{value}</strong>
    </div>
  );
}